import type { GetOrderResponse } from '../types'

import { useParams } from 'react-router-dom'

import { useAppDispatch } from '../../../store'
import * as previewerActions from '../../../store/modules/previewer'
import { NoDataPlaceHolder } from '../../PatientFolder/behavioral/components'
import { RemoteAttachment } from '../../shared/attachments'
import FriendlyDate from '../../shared/Date/FriendlyDate'
import { useUbaOrders, formatterPrice } from '../queries'
import { counsellingOptions } from '../types'

export const UbaOrdersListItem = ({ order }: { order: GetOrderResponse }): JSX.Element => {
  const dispatch = useAppDispatch()
  return (
    <div className='px-8 py-6 m-4 bg-zinc-100 rounded-md'>
      <div className='flex-1 flex flex-col justify-between'>
        <div className='flex flex-col text-zinc-600'>
          {(order?.products || []).map((p) => (
            <div
              key={p.id}
              className='flex justify-between gap-4 text-left py-5'
              style={{ borderBottom: '1px solid rgb(209, 213, 219)' }}
            >
              <span title={`din: ${p.din}`}>
                <span className='text-base text-zinc-900'>{p.product_name}</span>
                <span
                  className='ml-2'
                >{p.quantity > 1 ? `${formatterPrice.format(p.price)} x ${p.quantity}` : ''}
                </span>
              </span>
              <span className='text-right text-sm'>{formatterPrice.format(p.quantity * p.price)}</span>
            </div>
          ))}
        </div>

        <div className='mt-8 flex flex-col gap-2'>
          <p
            className='text-sm text-zinc-600'
            title='Shipping Address '
          >
            Shipping
            Address: {[ order?.shipping_country, order?.shipping_state, order?.shipping_city, order?.shipping_address ].filter(Boolean).join(', ')}
          </p>

          {order.shipping_description && order.shipping_price &&
            <p
              className='text-sm text-zinc-600'
              title='Shipping'
            >
              Shipping Method: {order.shipping_description} – {formatterPrice.format(Number(order.shipping_price))}
            </p>
          }

          {order?.tracking?.data?.map((d) => (
            <span
              title='Tracking details'
              className='text-sm text-zinc-600'
              key={d.trackingNumber}
            >
              Tracking #{' '}
              <a
                href={d.trackingLink.length ? d.trackingLink : '#'}
                target='_blank'
                rel='noreferrer'
              >
                {d.trackingNumber}
              </a>
            </span>
          ))}

          {typeof order.counseling === 'string' && order.counseling &&
            <p
              className='text-sm text-zinc-600'
              title='Counselling'
            >
              Counselling: {counsellingOptions.find((c) => c.value === Number(order.counseling))?.label}
            </p>
          }

          {!!order.comment?.length && (
            <p
              className='text-sm text-zinc-600'
              title='Comment'
            >
              Comment: {order.comment}
            </p>
          )}
        </div>

        <div className='flex flex-row gap-2 py-2 items-center relative'>
          {order.attachments?.map((a) => {
            return (
              <div
                className='relative'
                key={a.id}
              >
                <button
                  className='absolute top-0 left-0 h-full w-full z-10 cursor-pointer'
                  onClick={() => {
                    dispatch(
                      previewerActions.open({
                        mimeType: a.mimeType,
                        name: a.fileName,
                        id: a.id
                      }))
                  }}
                />
                <RemoteAttachment
                  attributes={{
                    style: {
                      width: 50,
                      height: 50,
                      zIndex: 0,
                      pointerEvents: 'none'
                    }
                  }}
                  document={{
                    id: a.id,
                    mime: a.mimeType,
                    title: a.fileName
                  }}
                />
              </div>
            )
          })}
        </div>
      </div>

      <div className='flex justify-between items-center mt-2'>
        <div className='flex items-center text-xs text-gray-500 gap-2'>
          <span title='Order Number'>#{order.order_number}</span>/
          <FriendlyDate date={order.order_date} />
        </div>
        <div className='flex flex-row gap-4 items-center'>
          <span
            className={`inline-block px-3 py-1 text-xs font-medium rounded ${
              order.status === 'Received'
                ? 'bg-green-100 text-green-600'
                : order.status === 'Future Fill'
                  ? 'bg-yellow-100 text-yellow-600'
                  : 'bg-red-100 text-red-600'
            }`}
          >
            {order.status}
          </span>

          <div className='text-lg font-semibold text-gray-800 mt-auto'>
            {formatterPrice.format(order.total)}
          </div>
        </div>
      </div>
    </div>
  )
}

export const UbaOrdersList = (): JSX.Element => {
  const { id } = useParams()
  const ordersQuery = useUbaOrders(id ?? '')

  const orders = ordersQuery.data

  if (!orders?.length) {
    return <NoDataPlaceHolder title='There are no orders to display.' />
  }

  return (
    <>
      {orders.map(order =>
        <UbaOrdersListItem
          key={order.id}
          order={order}
        />
      )}
    </>
  )
}
